import React, { useEffect, useState } from 'react';
import { Building2, Globe2, Briefcase, ChevronDown, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicContent, fetchPublicSettings, makeSectionCopyPicker } from '../services/publicContentService';

// Helper: build an array of {id, logo} entries from a counts list
const makePartners = (categoryKey, count) =>
  Array.from({ length: count }, (_, i) => ({
    id: `${categoryKey}-${i + 1}`,
    logo: `/partners/${categoryKey}/logo${i + 1}.png`,
  }));

// Default counts used as fallback until the admin populates /api/content/partners
const FALLBACK_COUNTS = { government: 27, international: 3, private: 49 };

const Partners = () => {
  const [showMore, setShowMore] = useState({
    government: false,
    international: false,
    private: false,
  });
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const { language } = useLanguage();

  const INITIAL_COUNT = 8;

  // CMS-driven partner lists per category. If empty, use the static file-based
  // partner logos (kept intact — /partners/<cat>/logoN.png filesystem paths).
  const [cmsBuckets, setCmsBuckets] = useState({}); // { government:[{id,logo}], ... }
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicContent('partners').then((items) => {
      if (!alive || !Array.isArray(items) || items.length === 0) return;
      const buckets = { government: [], international: [], private: [] };
      items.forEach((it) => {
        const cat = (it.category || '').toLowerCase();
        if (!buckets[cat]) return;
        if (!it.image) return;
        buckets[cat].push({
          id: it.id,
          logo: it.image,
          title_ar: it.title_ar || '',
          title_en: it.title_en || '',
        });
      });
      setCmsBuckets(buckets);
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const isAr = language === 'ar';
  const sec = makeSectionCopyPicker(sc, isAr);
  const partnersFor = (key) => {
    const cms = cmsBuckets[key] || [];
    return cms.length > 0 ? cms : makePartners(key, FALLBACK_COUNTS[key]);
  };

  const categories = [
    {
      id: 'government',
      title:
        language === 'ar'
          ? 'الجهات الحكومية والمؤسسات شبه الحكومية والهيئات المستقلة'
          : 'Government Entities, Semi-Government Institutions & Independent Authorities',
      icon: Building2,
      color: 'from-blue-500 to-cyan-600',
      ringColor: 'shadow-cyan-500/40',
      partners: partnersFor('government'),
    },
    {
      id: 'private',
      title: language === 'ar' ? 'القطاع الخاص' : 'Private Sector',
      icon: Briefcase,
      color: 'from-emerald-500 to-teal-600',
      ringColor: 'shadow-emerald-500/40',
      partners: partnersFor('private'),
    },
    {
      id: 'international',
      title:
        language === 'ar'
          ? 'الوكالات الإقليمية والدولية'
          : 'Regional and International Agencies',
      icon: Globe2,
      color: 'from-amber-500 to-orange-600',
      ringColor: 'shadow-orange-500/40',
      partners: partnersFor('international'),
    },
  ];

  const getVisiblePartners = (category) => {
    const all = category.partners;
    if (all.length <= INITIAL_COUNT) return all;
    return showMore[category.id] ? all : all.slice(0, INITIAL_COUNT);
  };

  const filteredCategories =
    activeCategory === 'all'
      ? categories
      : categories.filter((c) => c.id === activeCategory);

  return (
    <section id="partners" className="relative py-32 overflow-hidden">
      {/* Deep Ocean Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-900 via-blue-900 to-slate-900 dark:from-cyan-950 dark:via-blue-950 dark:to-slate-950" />

      {/* Light rays */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-cyan-400/60 via-cyan-400/20 to-transparent" />
        <div className="absolute top-0 left-2/4 w-px h-full bg-gradient-to-b from-blue-400/50 via-blue-400/15 to-transparent" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-gradient-to-b from-cyan-300/40 via-cyan-300/10 to-transparent" />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(28)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${4 + Math.random() * 6}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative container mx-auto px-4">
        {/* Section Title */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 data-testid="partners-heading" className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {sec('partners_heading', isAr ? 'شركاء النجاح' : 'Success Partners')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent mx-auto mb-6" />
          <p data-testid="partners-subtitle" className="text-lg text-cyan-100/80 max-w-3xl mx-auto leading-relaxed">
            {sec('partners_subtitle', isAr
              ? 'نفخر بشراكاتنا الاستراتيجية مع أبرز الجهات الحكومية والوكالات الدولية والقطاع الخاص'
              : 'We are proud of our strategic partnerships with leading government bodies, international agencies, and the private sector.')}
          </p>
        </div>

        {/* Filter Tabs */}
        <div
          className="flex flex-wrap justify-center gap-3 mb-16 animate-fade-in-up"
          style={{ animationDelay: '0.2s' }}
          data-testid="partners-filter-tabs"
        >
          {[
            { key: 'all', ar: 'الكل', en: 'All', activeBg: 'bg-cyan-500 shadow-cyan-500/50' },
            { key: 'government', ar: 'حكومي', en: 'Government', activeBg: 'bg-blue-500 shadow-blue-500/50' },
            { key: 'private', ar: 'خاص', en: 'Private', activeBg: 'bg-emerald-500 shadow-emerald-500/50' },
            { key: 'international', ar: 'دولي', en: 'International', activeBg: 'bg-orange-500 shadow-orange-500/50' },
          ].map((tab) => (
            <button
              key={tab.key}
              data-testid={`partners-tab-${tab.key}`}
              onClick={() => setActiveCategory(tab.key)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === tab.key
                  ? `${tab.activeBg} text-white shadow-lg`
                  : 'bg-white/10 text-slate-200 hover:bg-white/20 backdrop-blur-sm border border-white/10'
              }`}
            >
              {language === 'ar' ? tab.ar : tab.en}
            </button>
          ))}
        </div>

        {/* Partner Categories */}
        <div className="space-y-24">
          {filteredCategories.map((category, catIndex) => {
            const Icon = category.icon;
            const visible = getVisiblePartners(category);
            const hasMore = category.partners.length > INITIAL_COUNT;

            return (
              <div
                key={category.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${catIndex * 0.15}s` }}
                data-testid={`partners-category-${category.id}`}
              >
                {/* Category Header */}
                <div className="text-center mb-12">
                  <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white max-w-3xl">
                      {category.title}
                    </h3>
                  </div>
                  <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent mx-auto" />
                </div>

                {/* Bubble Cluster */}
                <div className="flex flex-wrap justify-center gap-6 md:gap-8 max-w-6xl mx-auto">
                  {visible.map((partner, index) => (
                    <button
                      key={partner.id}
                      data-testid={`partner-bubble-${partner.id}`}
                      onClick={() => setSelectedPartner({ ...partner, category })}
                      className="relative group focus:outline-none"
                      style={{
                        animation: `float ${3.5 + (index % 5) * 0.4}s ease-in-out infinite`,
                        animationDelay: `${(index % 6) * 0.25}s`,
                      }}
                    >
                      {/* Glow on hover */}
                      <div
                        className={`absolute inset-0 rounded-full bg-gradient-to-br ${category.color}
                                    blur-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500`}
                      />

                      {/* Bubble */}
                      <div
                        className={`relative w-28 h-28 md:w-32 md:h-32 rounded-full
                                    bg-white dark:bg-white
                                    flex items-center justify-center
                                    border-4 border-white/30 dark:border-cyan-500/30
                                    shadow-2xl
                                    transition-all duration-300
                                    group-hover:scale-110 group-hover:border-white/60
                                    group-hover:shadow-cyan-300/60`}
                      >
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white flex items-center justify-center p-2 overflow-hidden">
                          <img
                            src={partner.logo}
                            alt={`Partner ${partner.id}`}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentNode.innerHTML =
                                '<div class="text-3xl font-bold text-slate-300">EBC</div>';
                            }}
                          />
                        </div>
                      </div>

                      {/* Ripple effect */}
                      <div className="absolute inset-0 rounded-full pointer-events-none">
                        <div
                          className="absolute inset-0 rounded-full border-2 border-white/50
                                     animate-ping opacity-0 group-hover:opacity-75"
                          style={{ animationDuration: '1.5s' }}
                        />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Show More / Less */}
                {hasMore && (
                  <div className="text-center mt-10">
                    <button
                      data-testid={`partners-show-more-${category.id}`}
                      onClick={() =>
                        setShowMore((prev) => ({
                          ...prev,
                          [category.id]: !prev[category.id],
                        }))
                      }
                      className="group px-8 py-3 rounded-full bg-white/10 hover:bg-white/20
                               backdrop-blur-sm border border-white/20 hover:border-white/40
                               text-white font-medium transition-all duration-300
                               hover:shadow-lg hover:shadow-cyan-500/30"
                    >
                      <span className="flex items-center gap-2">
                        {showMore[category.id]
                          ? language === 'ar'
                            ? 'عرض أقل'
                            : 'Show Less'
                          : language === 'ar'
                            ? `عرض المزيد (${category.partners.length - INITIAL_COUNT}+)`
                            : `Show More (${category.partners.length - INITIAL_COUNT}+)`}
                        <ChevronDown
                          className={`w-5 h-5 transition-transform duration-300 ${
                            showMore[category.id] ? 'rotate-180' : ''
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Partner Detail Modal */}
      {selectedPartner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedPartner(null)}
          data-testid="partner-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

          <div
            className="relative max-w-lg w-full bg-gradient-to-br from-cyan-500 to-blue-600
                       rounded-3xl p-1 animate-scale-in shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-slate-900/95 backdrop-blur-xl rounded-3xl p-8">
              <button
                onClick={() => setSelectedPartner(null)}
                data-testid="partner-modal-close"
                className="absolute top-4 right-4 p-2 rounded-full
                         bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              <div className="text-center">
                <div
                  className="w-32 h-32 mx-auto mb-6 rounded-full bg-white
                            flex items-center justify-center shadow-2xl p-4"
                >
                  <img
                    src={selectedPartner.logo}
                    alt={`Partner ${selectedPartner.id}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>

                <div className="inline-block px-4 py-1 rounded-full bg-white/10 border border-white/20 mb-4">
                  <span className="text-sm text-cyan-200">
                    {selectedPartner.category?.title}
                  </span>
                </div>

                <p className="text-slate-200 leading-relaxed">
                  {language === 'ar'
                    ? 'شريك استراتيجي يساهم في تحقيق أهدافنا البيئية والتنموية المستدامة.'
                    : 'A strategic partner contributing to our sustainable environmental and development goals.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Partners;
